import classnames from 'classnames';
import { getSimplifiedDate } from 'helpers/dateHelper';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import ScoreDisplay from 'components/Score/ScoreDisplay';
import UpdatedStatusLabel from 'components/Score/components/UpdatedStatusLabel';
import ScoreChangeLabel from 'components/Score/components/ScoreChangeLabel';
import Label from 'components/form/Label/Label';
import { Link } from 'components/navigation/Link/Link';
import { ReactComponent as Plus } from 'style/_assets/icons/plus.svg';
import { ReactComponent as TuLogo } from 'style/_assets/icons/tu_logo_teal.svg';
import { ReactComponent as EquifaxLogo } from 'style/_assets/logos/equifax_logo.svg';
import { ReactComponent as EquifaxLogoDisabled } from 'style/_assets/logos/equifax_logo_disabled.svg';
import { ReactComponent as ExperianLogo } from 'style/_assets/logos/experian_logo.svg';
import { ReactComponent as ExperianLogoDisabled } from 'style/_assets/logos/experian_logo_disabled.svg';
import { BUREAU_ENUMS } from 'pages/RedesignDashboard/constants';
import 'components/Score/Score.scss';
import './ScoreDial.scss';
import { isPlus1B, isFreeTrial, getUserRedirectPath } from 'helpers/userHelper';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { SUBSCRIPTION_VERTICAL } from 'types/mrph.vertical.constants';
import { providerMapping } from 'customHelpersPath/factorMapping.helper';
import { ConditionalRendering } from 'helpers/conditionalRedering.helper';
import { useSelector } from 'react-redux';
import { canPurchase3BReport } from 'pages_CreditReportTU/creditReportTU3B.helper';
import { SHOW_3B_DIAL_LINK } from 'pages_GraphScore/GraphScore.constants';
import { isFn } from 'helpers/objectHelpers';

const CreditBureauLogoMap = {
  [BUREAU_ENUMS.TRANS_UNION]: (
    <div id="tu_icon" aria-label="tu_icon" role="img">
      <TuLogo height={18} />
    </div>
  ),
  [BUREAU_ENUMS.EQUIFAX]: (
    <EquifaxLogo height={18} aria-label="equifax-logo" role="img" />
  ),
  [BUREAU_ENUMS.EXPERIAN]: (
    <ExperianLogo height={18} aria-label="experian-logo" role="img" />
  ),
};

const EmptyCreditBureauLogoMap = {
  [BUREAU_ENUMS.TRANS_UNION]: (
    <div id="tu_icon" aria-label="tu_icon" role="img">
      <TuLogo height={18} />
    </div>
  ),
  [BUREAU_ENUMS.EQUIFAX]: (
    <EquifaxLogoDisabled
      height={18}
      aria-label="equifax-logo-disabled"
      role="img"
    />
  ),
  [BUREAU_ENUMS.EXPERIAN]: (
    <ExperianLogoDisabled
      height={18}
      aria-label="experian-logo-disabled"
      role="img"
    />
  ),
};

export function EmptyCreditScoreDial(props) {
  return (
    <>
      <FlexContainer className="justify-center">
        {EmptyCreditBureauLogoMap[props?.bureau]}
      </FlexContainer>
      <FlexContainer
        className={classnames(
          'justify-center text-12 padB10 padT6 secondary_text_light bold',
        )}
      >
        {props.config?.dashboardStyle?.gradeDial?.emptyScoreText}
      </FlexContainer>
      <FlexContainer
        className={classnames('emptyDialItemInsideContainer', {
          'flex-column':
            props.config?.dashboardStyle?.gradeDial?.hasTextUnderPlus,
        })}
      >
        {props.potentialCreditProfileContainerError ? (
          '...'
        ) : props.config?.dashboardStyle?.gradeDial?.hasTextUnderPlus ? (
          <>
            <Label className="margB2" tabIndex="-1">
              <Plus aria-label="plus-icon" />
            </Label>
            <Label
              className={'caption m-text-10 secondary_text_light'}
              tabIndex="-1"
            >
              Upgrade
            </Label>
          </>
        ) : (
          <Plus aria-label="plus-icon" />
        )}
      </FlexContainer>
    </>
  );
}

export function FullCreditScoreDial(props) {
  const {
    bureau,
    score,
    scoreDate,
    scoreChange = 0,
    handleClick,
    datatestid,
    currentBureau,
    isSomeBureauClicked,
    isSomeFactorClicked,
    isPotentialView,
    isFree1BUser,
    className,
    isUpdateScoreAvailable,
    showDonut,
    potentialCreditProfileContainerError,
    redirectTo,
    config,
  } = props;
  const { alaCartePurchaseFlowType } = config ?? {};
  const isFreeOr1BUser = bureau === BUREAU_ENUMS.TRANS_UNION;
  return (
    <div
      className={className}
      aria-label={`${bureau} score ${score}`}
      role="group"
      tabIndex={'0'}
    >
      <FlexContainer
        className={classnames('justify-center', {
          potentialViewIcons: props.todayContainerLoading || isPotentialView,
        })}
      >
        {CreditBureauLogoMap[bureau]}
      </FlexContainer>
      <div className={isPotentialView ? '' : 'padB12'}>
        {isPotentialView ? (
          <ConditionalRendering
            isTrue={!props.todayContainerLoading && showDonut}
          >
            <ScoreChangeLabel
              scoreChange={scoreChange}
              scoreDate={scoreDate}
              isPotentialView={isPotentialView}
              isFreeOr1BUser={isFreeOr1BUser}
            ></ScoreChangeLabel>
          </ConditionalRendering>
        ) : (
          <ConditionalRendering isTrue={showDonut || !showDonut}>
            <UpdatedStatusLabel
              content={isFreeOr1BUser ? 'Updated' : 'Updated:'}
              color={'fontGray500'}
              showDaily={isFreeOr1BUser}
              lastUpdateTime={getSimplifiedDate(
                isFreeOr1BUser ? new Date() : scoreDate,
              )}
              isUpdateScoreAvailable={isUpdateScoreAvailable}
              showDonut={showDonut || (!showDonut && score)}
              redirectTo={redirectTo}
              alaCartePurchaseFlowType={alaCartePurchaseFlowType}
              todayContainerLoading={props.todayContainerLoading}
              isTransunion={bureau === BUREAU_ENUMS.TRANS_UNION}
              score={score ? score : null}
              hideOverviewDialScoreAndLabel={
                config?.hideOverviewDialScoreAndLabel
              }
            />
          </ConditionalRendering>
        )}
      </div>
      <div
        className={classnames('dialItemInsideContainer', {
          highlighted: datatestid.includes(currentBureau),
          potentialView: props.todayContainerLoading || isPotentialView,
        })}
      >
        <ScoreDisplay
          className="dialItem singleScoreExpDialRedesign"
          definedWidth={54}
          score={potentialCreditProfileContainerError ? '...' : score}
          datatestid={datatestid}
          bureau={bureau}
          onClickItem={handleClick}
          scoreChange={scoreChange}
          mobileView={true}
          showRatingText={true}
          showOnlyRatingScore={true}
          showOnlyRating={false}
          isSmallScoreText={true}
          isPotentialView={isPotentialView}
          showDonut={showDonut}
          todayContainerLoading={props.todayContainerLoading}
        />
        {bureau === BUREAU_ENUMS.TRANS_UNION && (
          <div
            className={classnames('skewed-line-left', {
              freeUser: isFree1BUser,
              showingInfo: isSomeFactorClicked,
              highlighted: isSomeBureauClicked && currentBureau === datatestid,
              hiddenLine: isSomeBureauClicked && currentBureau !== datatestid,
              potentialView: props.todayContainerLoading || isPotentialView,
            })}
          ></div>
        )}
        {bureau === BUREAU_ENUMS.EQUIFAX && (
          <div
            className={classnames('vertical-line', {
              showingInfo: isSomeFactorClicked,
              highlighted: isSomeBureauClicked && currentBureau === datatestid,
              hiddenLine: isSomeBureauClicked && currentBureau !== datatestid,
              potentialView: props.todayContainerLoading || isPotentialView,
            })}
          ></div>
        )}
        {bureau === BUREAU_ENUMS.EXPERIAN && (
          <div
            className={classnames('skewed-line-right', {
              showingInfo: isSomeFactorClicked,
              highlighted: isSomeBureauClicked && currentBureau === datatestid,
              hiddenLine: isSomeBureauClicked && currentBureau !== datatestid,
              potentialView: props.todayContainerLoading || isPotentialView,
            })}
          ></div>
        )}
      </div>
    </div>
  );
}

const CreditScoreDials3Bureau = props => {
  const {
    creditScoreDetails,
    isSomeBureauClicked,
    currentBureau,
    handleClick,
    userInfo,
    subscriptionInfo,
  } = props;

  const creditReportRes = useSelector(
    state => state?.creditReport?.creditReports,
  );

  const is1B = isPlus1B(userInfo);
  const isShow3BDialLink = isFn(SHOW_3B_DIAL_LINK)
    ? SHOW_3B_DIAL_LINK(userInfo, subscriptionInfo)
    : SHOW_3B_DIAL_LINK;

  const isDownloadAvailable = canPurchase3BReport({
    userInfo,
    creditReportRes,
  });

  const redirectTo = is1B ? '/plus/upgrade' : null;

  return creditScoreDetails?.map((item, i) => {
    return (
      <FullCreditScoreDial
        key={`${item.bureau}-${i}`}
        showVerticalLine={item.bureau === BUREAU_ENUMS.TRANS_UNION}
        {...{
          ...item,
          scoreChange: item.quarterDifference || item.scoreChange,
        }}
        handleClick={handleClick}
        datatestid={item.bureau}
        className={classnames('dialItemContainer', {
          grayMore: isSomeBureauClicked && currentBureau !== item.bureau,
        })}
        redirectTo={redirectTo}
        isUpdateScoreAvailable={
          currentBureau === item.bureau &&
          isDownloadAvailable &&
          item.bureau !== BUREAU_ENUMS.TRANS_UNION &&
          isShow3BDialLink
        }
        {...props}
      />
    );
  });
};

const CreditScoreDials1Bureau = props => {
  const {
    transUnionDataForUser,
    userInfo,
    eventHandler,
    isPotentialView,
    potentialCreditProfileContainerError,
  } = props;

  const score = transUnionDataForUser?.score;
  let redirectPath = getUserRedirectPath(props);
  const handleEmptyCreditScoreLinkRedirect = bureau => {
    const clickTypeValue =
      props?.config?.dashboardStyle?.gradeDial?.clickType ?? '';
    eventHandler(CLICK, {
      'Click Type': providerMapping.get(bureau) + ' ' + clickTypeValue,
      'Page Position': 'Premium',
      'Product Offer': 'Premium',
      'Module Name': `${
        isPotentialView ? 'Potential' : 'Today'
      } Credit Score Dial`,
      'Plan type': isFreeTrial(userInfo) ? 'Free Trial Turbo' : 'Free',
      Vertical: SUBSCRIPTION_VERTICAL,
      'Module Position': 1,
    });
  };
  return (
    <>
      <FullCreditScoreDial
        className="dialitemcontainer1B"
        datatestid={BUREAU_ENUMS.TRANS_UNION}
        bureau={BUREAU_ENUMS.TRANS_UNION}
        score={score}
        {...props}
      />
      {[BUREAU_ENUMS.EQUIFAX, BUREAU_ENUMS.EXPERIAN].map(item => {
        return (
          <div
            key={item}
            className="emptyDialItemContainer"
            onClick={() => handleEmptyCreditScoreLinkRedirect(item)}
          >
            <Link to={redirectPath} disableAriaLabel>
              <EmptyCreditScoreDial
                bureau={item}
                potentialCreditProfileContainerError={
                  potentialCreditProfileContainerError
                }
                config={props.config}
              />
            </Link>
          </div>
        );
      })}
    </>
  );
};

const ThreeCreditScoreDials = props => {
  const { isPotentialView, creditScoreDetails } = props;

  if (isPotentialView) {
    const is1B = creditScoreDetails?.filter(item => item.score).length === 1;
    if (is1B) {
      return (
        <CreditScoreDials1Bureau
          {...{
            ...props,
            scoreChange: creditScoreDetails?.[0].scoreChange,
          }}
        />
      );
    } else {
      return <CreditScoreDials3Bureau {...props} />;
    }
  } else {
    return props.creditScoreDetails?.length > 0 ? (
      <CreditScoreDials3Bureau {...props} />
    ) : (
      <CreditScoreDials1Bureau {...props} />
    );
  }
};

export default function CreditScoreDial(props) {
  const {
    creditScore,
    currentBureau,
    handleClick,
    isSomeFactorClicked,
    isPotentialView,
  } = props;
  // Check if only one bureau
  const availableBureaus = creditScore?.filter(({ score }) => score);
  const isFree1BUser = availableBureaus?.length === 1;
  const transUnionDataForUser = isFree1BUser
    ? { score: creditScore?.[0]?.score }
    : undefined;

  const creditScoreDetails =
    isFree1BUser && !isPotentialView ? [] : creditScore;

  return (
    <FlexContainer
      className={classnames('CreditScoreDial', {
        freeUser: isFree1BUser,
        showingInfo: isSomeFactorClicked,
        potentialView: props.todayContainerLoading || isPotentialView,
      })}
    >
      <ThreeCreditScoreDials
        handleClick={handleClick}
        creditScoreDetails={creditScoreDetails}
        currentBureau={currentBureau}
        transUnionDataForUser={transUnionDataForUser}
        isFree1BUser={isFree1BUser}
        {...props}
      />
    </FlexContainer>
  );
}
